<template>
	<div class="arbitrary-data-wrapper">
		<form v-if="searchOn" id="arbitrary-data-filter">
			<b-form inline class="attendees-filter-form-group">
				<label class="sr-only" for="inline-form-input-name">Search</label>
				<b-input
					class="form-control"
					type="search"
					id="arbitrary-data-filter-input"
					v-model="filter"
					:placeholder="text.getByKey('searchPlaceholder', 'Type to Search')"
					style="width:80%;"
				/>
				<b-button type="reset" v-bind:disabled="!filter" v-on:click="filter = ''" class="btn btn-primary" style="width:20%;">
					{{ text.getByKey("clearSearch", "Clear") }}
				</b-button>
			</b-form>
		</form>
		<b-list-group v-if="!this.isBusy" class="agenda" :style="agendaStyle.group">
			<b-list-group-item v-for="item in sortedItems" :key="item.id" class="flex-column align-items-start row-striped">
				<b-container>
					<span v-if="groupHeader(item, sortedItems)">
						<b-row class="mb-2">
							<b-col cols="12" class="surrounded font-weight-bold" :style="agendaStyle.groupHeader">{{
								groupHeader(item, sortedItems)
							}}</b-col>
						</b-row>
					</span>
					<b-row v-if="item.description" class="text-left mb-1 text-toggle g-0" v-b-toggle="'collapse-description-' + item.id">
						<b-col cols="1" class="text-center">
							<b-icon-plus-square
								class="text-collapsed"
								:title="text.getByKey('expandTitle', 'Expand')"
								:aria-label="text.getByKey('expandTitle', 'Expand')"
							></b-icon-plus-square>
							<b-icon-dash-square
								class="text-expanded"
								:title="text.getByKey('collapseTitle', 'Collapse')"
								:aria-label="text.getByKey('collapseTitle', 'Collapse')"
							></b-icon-dash-square>
						</b-col>
						<b-col cols="10" class="title text-left font-weight-bold p-0">{{ text.get(item.title) }}</b-col>
					</b-row>
					<b-row v-else class="text-left mb-1 text-toggle g-0">
						<b-col cols="1" class="text-center"> </b-col>
						<b-col cols="10" class="title text-left font-weight-bold p-0">{{ text.get(item.title) }}</b-col>
					</b-row>
					<b-collapse :id="'collapse-description-' + item.id">
						<b-row class="text-left mb-1" :title="text.getByKey('descriptionTitle', 'Full Description')">
							<b-col cols="1" class="text-center"> </b-col>
							<b-col cols="10" class="text-left p-0">
								{{ longDescription(item) }}
							</b-col>
						</b-row>
					</b-collapse>
					<span v-if="Object.keys(calendarTypes).length === 1">
						<add-to-calendar
							style="width:100%"
							v-bind:eventData="calEvent(item)"
							v-bind:type="Object.keys(calendarTypes)[0]"
							v-bind:styleOverride="calendarStyle"
							renderless
							v-bind:analyticsContext="analyticsContext"
						>
							<template v-slot:generate="{ generate }">
								<b-row class="mb-1  g-0" v-on:click="generate">
									<b-col cols="1" class="text-center">
										<b-icon-calendar-plus
											:title="text.getByKey('addToCalendarIconTitle', 'Add to Calendar')"
											:aria-label="text.getByKey('addToCalendarIconTitle', 'Add to Calendar')"
										></b-icon-calendar-plus>
									</b-col>
									<b-col cols="10" class="text-left p-0">{{ formatEventTime(item) }}</b-col>
								</b-row>
							</template>
						</add-to-calendar>
					</span>
					<span v-else>
						<div v-b-modal="'agendamodal' + item.id">
							<b-row class="mb-1  g-0">
								<b-col cols="1" class="text-center">
									<b-icon-calendar-plus
										:title="text.getByKey('addToCalendarIconText', 'Add to Calendar')"
									></b-icon-calendar-plus>
								</b-col>
								<b-col cols="10" class="text-left p-0">{{ formatEventTime(item) }}</b-col>
							</b-row>
						</div>
						<b-modal
							:title="text.getByKey('addToCalendarIconText', 'Add to Calendar')"
							v-bind:id="'agendamodal' + item.id"
							centered
							size="lg"
							hide-footer
						>
							<span v-for="(description, type) in calendarTypes" v-bind:key="type">
								<add-to-calendar
									style="width:100%"
									v-bind:eventData="calEvent(item)"
									v-bind:type="type"
									v-bind:description="text.get(item.title)"
									v-bind:styleOverride="calendarStyle"
									v-bind:analyticsContext="analyticsContext"
								>
								</add-to-calendar>
								<br />
							</span>
						</b-modal>
					</span>
					<span v-for="(address, index) in item.addresses" :key="index">
						<b-row v-if="address.type === 'uri'" class="text-left mb-1">
							<b-col cols="1" class="text-center"> </b-col>
							<b-col cols="10" class="text-left p-0">
								{{ text.get(address.name) }}
							</b-col>
						</b-row>

						<b-row v-else class="text-left mb-1  g-0">
							<b-col cols="1" class="text-center"> </b-col>
							<b-col cols="10" class="text-left p-0">
								{{ text.get(address.name) }}
							</b-col>
						</b-row>
					</span>
					<b-row v-if="item.speakers && item.speakers.length > 0" class="text-left mb-1  g-0">
						<b-col cols="1" class="text-center">
							<b-icon-person :title="text.getByKey('speakerTitle', 'Speakers')"></b-icon-person>
						</b-col>
						<b-col cols="10" class="title text-left p-0">{{ speakers(item) }}</b-col>
					</b-row>
				</b-container>
				<div class="w-100 mb-1"></div>
			</b-list-group-item>
		</b-list-group>
		<b-spinner v-else class="m-5 text-dark" :label="text.getByKey('loading', 'Loading ...')"></b-spinner>
	</div>
</template>

<script>
import { log } from "@/logging";
import { BIconCalendarPlus, BIconPlusSquare, BIconDashSquare, BIconPerson } from "bootstrap-vue";
import AddToCalendar from "@/components/calendar/AddToCalendar.vue";
//import { AgendaService } from "@/agenda"

const myLoggingName = "ArbitraryDataAsAgenda";
const fields = ["id", "title", "description", "start", "end", "addresses", "speakerIds"];

export default {
	props: {
		agendaSource: String,
		speakerSource: String,
		searchOn: Boolean,
		analyticsContext: Object,
		agendaStyle: Object,
		calendarTypes: Object,
		calendarStyle: Object,
		text: Object
	},
	data: () => {
		return {
			arbitraryData: [],
			state: {},
			isBusy: true,
			filter: "",
			sortKey: "start",
			siteConfig: null
		};
	},
	name: "ArbitraryDataAsAgenda",
	inject: ["authService", "configService", "agendaService"],
	components: {
		BIconCalendarPlus,
		BIconPlusSquare,
		BIconDashSquare,
		BIconPerson,
		AddToCalendar
	},
	// Maybe use pin-map or pin-map-fill for both location types or physical
	mounted() {
		this.isBusy = true;
		let capturedVue = this;
		this.dataProvider()
			.then(() => {
				return this.configService.getSiteConfig();
			})
			.then(config => {
				capturedVue.siteConfig = config;
			});
	},
	computed: {
		displayFields: function() {
			const rc = fields.map(i => i);
			return rc;
		},
		filteredItems: function() {
			if (this.filter) {
				return this.arbitraryData.filter(item => {
					let decorations = {};
					decorations.displayStartTime = this.formatEventTime(item);
					const options = { weekday: "long", year: "numeric", month: "long", day: "numeric", timeZoneName: "short" };
					decorations.displayStartDate = new Intl.DateTimeFormat(this.culture(), options).format(item.start).replace(" at ", "");
					decorations.virtual = item.addresses.findIndex(item => item.type === "uri") >= 0;
					decorations.physical = item.addresses.findIndex(item => item.type === "physical") >= 0;
					return (JSON.stringify(item) + JSON.stringify(decorations)).toLowerCase().includes(this.filter.toLowerCase());
				});
			}
			return this.arbitraryData;
		},
		sortedItems: function() {
			let rc = this.filteredItems;
			switch (this.sortKey) {
				case "start":
					//JM ked-357 properly sort by ID
					rc = rc.toSorted((a, b) => a.id - b.id);
					break;
				default:
					break;
			}

			return rc;
		}
	},
	methods: {
		culture() {
			const language = this.text.getSelectedLanguage();
			switch (language.key.split("-")[0]) {
				case "fr":
					return "fr-CA";
				default:
					return "en-US";
			}
		},
		// loadingStyle() {
		// 	let style = { color: this.common.globalStyleVariables["primary-text-colour"] };
		// 	return style;
		// },
		groupHeader(item, sortedItems) {
			let rc = undefined;
			let first = undefined;
			switch (this.sortKey) {
				case "start":
					first = sortedItems.find(testItem => testItem.start.toDateString() === item.start.toDateString());
					if (first) {
						if (item.id === first.id) {
							//							const options = { weekday: "long", year: "numeric", month: "long", day: "numeric", timeZoneName: "short" };
							const options = { weekday: "long", year: "numeric", month: "long", day: "numeric" };
							rc = new Intl.DateTimeFormat(this.culture(), options).format(item.start);
							// DateTimeFormat inserts " at " between the year and time zone - not appropriate when just displaying the date, so it is manually removed.
							rc = rc.replace(" at ", " ");
						}
					}
					break;
				default:
					break;
			}
			return rc;
		},
		isEndNextDay(startTime, endTime) {
			const symd = startTime.getFullYear() * 10000 + startTime.getMonth() * 100 + startTime.getDate();
			const eymd = endTime.getFullYear() * 10000 + endTime.getMonth() * 100 + endTime.getDate();
			return eymd > symd;
		},
		formatEventTime(item) {
			if (item.end) {
				const nextDay = this.isEndNextDay(item.start, item.end) ? this.text.getByKey("nextDay", "Next Day") : "";
				return this.formatDateTime(item.start) + " - " + this.formatDateTime(item.end) + " " + nextDay;
			} else {
				return this.formatDateTime(item.start);
			}
		},
		formatDateTime(dateTime) {
			const options = {
				hour: "numeric",
				minute: "numeric",
				second: "numeric",
				hour12: true,
				timeZoneName: "short"
			};
			return new Intl.DateTimeFormat(this.culture(), options).format(dateTime);
		},
		longDescription(item) {
			if (item.description) {
				return this.text.get(item.description);
			}
			return "";
		},
		speakers(item) {
			const capturedVue = this;
			return item.speakers.map(it => capturedVue.text.get(it.name)).join(", ");
		},
		calEvent(item) {
			const start = new Date(item.start);
			const end = item.end ? new Date(item.end) : undefined;
			// Use first addresses of type uri for the url
			const url = item.addresses.find(it => it.type === "uri")?.uri;
			// Use the name of the first address for the location name
			const name = item.addresses.length > 0 ? item.addresses[0].name : "";
			return {
				title: this.text.get(item.title),
				location: this.text.get(name),
				description: this.text.get(item.description),
				start: start,
				end: end,
				url: url,
				recurrence: null
			};
		},

		dataProvider: async function() {
			// Can this be switched to fully async
			this.isBusy = true;
			try {
				const token = await this.authService.getToken();
				const agendas = await this.agendaService.getAgenda(token, this.agendaSource, this.speakerSource);

				this.isBusy = false;
				this.arbitraryData = agendas;
			} catch (err) {
				log(myLoggingName, "dataProvider error " + err);
				this.isBusy = false;
				this.arbitraryData = [];
			}
		}
	}
};
</script>
<style scoped lang="scss">
@import "../scss/constants";

.arbitrary-data-wrapper {
	max-width: 99%;
	display: block;
	margin: 0 auto;
	background-color: white;
	max-height: 90%;
}

.agenda {
	position: relative;
	overflow-y: scroll;
	color: black;
}

.title {
	color: black;
}

.surrounded {
	overflow: hidden;
	text-align: left;
	color: black;
}
// For now, keep this as an example of how to centre text and surround with horizontal lines
// .surrounded:before,
// .surrounded:after {
// 	background-color: #000;
// 	content: "";
// 	display: inline-block;
// 	height: 1px;
// 	position: relative;
// 	vertical-align: middle;
// 	width: 50%;
// }

// .surrounded:before {
// 	right: 0.5em;
// 	margin-left: -50%;
// }

// .surrounded:after {
// 	left: 0.5em;
// 	margin-right: -50%;
// }

.text-toggle[aria-expanded="false"] .text-expanded {
	display: none;
}
.text-toggle[aria-expanded="true"] .text-collapsed {
	display: none;
}

.row-striped:nth-of-type(odd) {
	background-color: #efefef;
}

.row-striped:nth-of-type(even) {
	background-color: #ffffff;
}

@media (max-width: $screen-mobile-max) {
	.arbitrary-data-wrapper {
		position: relative;
		width: 100%;
	}
}
</style>
